import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { analyticsService } from "../../services/analytics.service";
import {
  WorkforceAnalysisResponse,
  KpiQueryParams,
  SeniorityStats,
} from "../../types/kpi.types";

const shimmer = `
  @keyframes shimmer {
    0%   { background-position: -400px 0; }
    100% { background-position: 400px 0; }
  }
`;

const SkeletonBar: React.FC<{ width: number | string; height: number }> = ({
  width,
  height,
}) => (
  <>
    <style>{shimmer}</style>
    <div
      style={{
        width,
        height,
        borderRadius: height / 2,
        background:
          "linear-gradient(90deg, #ececec 25%, #f5f5f5 50%, #ececec 75%)",
        backgroundSize: "800px 100%",
        animation: "shimmer 1.4s infinite linear",
        margin: "6px 0",
      }}
    />
  </>
);

const DESCRIPTIONS = {
  payro: {
    card: "Employees registered in Payro (downloaded the app)",
    active: "Worked a shift in the last 60 days",
    left: "No shift recorded in 60+ days",
  },
  nonPayro: {
    card: "Employees in the company vault but never registered in Payro",
    active: "Worked a shift in the last 60 days",
    left: "No shift recorded in 60+ days",
  },
};

interface StatCardProps {
  title: string;
  description: string;
  accentColor: string;
  total: number;
  active: number;
  activeDesc: string;
  left: number;
  leftDesc: string;
  turnoverPct: number;
  loading?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  description,
  accentColor,
  total,
  active,
  activeDesc,
  left,
  leftDesc,
  turnoverPct,
  loading,
}) => (
  <div
    style={{
      backgroundColor: "#fff",
      borderRadius: 10,
      padding: "28px 32px",
      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
      border: `2px solid ${accentColor}`,
    }}
  >
    <h3
      style={{
        margin: "0 0 4px",
        color: accentColor,
        fontSize: 16,
        fontWeight: 700,
      }}
    >
      {title}
    </h3>
    <div style={{ fontSize: 12, color: "#999", marginBottom: 16 }}>
      {description}
    </div>
    {loading ? (
      <>
        <SkeletonBar width='60%' height={36} />
        <SkeletonBar width='80%' height={16} />
        <SkeletonBar width='80%' height={16} />
        <SkeletonBar width='60%' height={16} />
      </>
    ) : (
      <>
        <div
          style={{
            fontSize: 40,
            fontWeight: 800,
            color: "#1a1a2e",
            marginBottom: 16,
          }}
        >
          {total.toLocaleString()}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <StatRow
            label='Active'
            sublabel={activeDesc}
            value={active}
            color='#4CAF50'
          />
          <StatRow
            label='Left'
            sublabel={leftDesc}
            value={left}
            color='#ef5350'
          />
          <div
            style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid #eee" }}
          >
            <span style={{ fontSize: 13, color: "#888" }}>Turnover rate: </span>
            <span
              style={{
                fontSize: 15,
                fontWeight: 700,
                color: turnoverPct > 15 ? "#ef5350" : "#ff9800",
              }}
            >
              {turnoverPct}%
            </span>
          </div>
        </div>
      </>
    )}
  </div>
);

const StatRow: React.FC<{
  label: string;
  sublabel?: string;
  value: number;
  color: string;
}> = ({ label, sublabel, value, color }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
    }}
  >
    <div>
      <span style={{ fontSize: 14, color: "#555" }}>{label}</span>
      {sublabel && (
        <div style={{ fontSize: 11, color: "#bbb", marginTop: 1 }}>
          {sublabel}
        </div>
      )}
    </div>
    <span
      style={{
        fontSize: 16,
        fontWeight: 700,
        color,
        flexShrink: 0,
        marginLeft: 8,
      }}
    >
      {value.toLocaleString()}
    </span>
  </div>
);

const ShiftCard: React.FC<{
  title: string;
  accentColor: string;
  value: number;
  months: number;
}> = ({ title, accentColor, value, months }) => (
  <div
    style={{
      backgroundColor: "#fff",
      borderRadius: 10,
      padding: "20px 32px",
      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
      border: `1px solid ${accentColor}33`,
      display: "flex",
      alignItems: "center",
      gap: 20,
    }}
  >
    <div>
      <div style={{ fontSize: 13, color: "#888", marginBottom: 4 }}>
        {title}
      </div>
      <div style={{ fontSize: 32, fontWeight: 800, color: accentColor }}>
        {value}
      </div>
      <div style={{ fontSize: 12, color: "#bbb", marginTop: 2 }}>
        over {months} months of data
      </div>
    </div>
  </div>
);

const PRINT_CSS = `
@media print {
  body * { visibility: hidden; }
  #workforce-print-section,
  #workforce-print-section * { visibility: visible; }
  #workforce-print-section {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    padding: 24px;
    box-sizing: border-box;
  }
  .print-header { display: block !important; }
  .print-hide { display: none !important; }
  * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
}
.print-header { display: none; }
`;

function formatAvgSeniority(months: number): string {
  const years = Math.floor(months / 12);
  const rem = months % 12;
  if (years === 0) return `${rem}m`;
  if (rem === 0) return `${years}y`;
  return `${years}y ${rem}m`;
}

const SeniorityTooltip: React.FC<any> = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e0e0e0",
        borderRadius: 8,
        padding: "10px 14px",
        fontSize: 13,
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 6 }}>{label}</div>
      {payload.map((p: any) => (
        <div key={p.dataKey} style={{ color: p.color, marginBottom: 2 }}>
          {p.name}: <strong>{p.value.toFixed(1)}%</strong>{" "}
          <span style={{ color: "#888", fontWeight: 400 }}>
            ({p.payload[p.dataKey === "payroPercent" ? "payro" : "nonPayro"].toLocaleString()} employees)
          </span>
        </div>
      ))}
    </div>
  );
};

const SenioritySection: React.FC<{ seniority: SeniorityStats }> = ({ seniority }) => {
  const { buckets, avgMonths, withoutStartDate } = seniority;
  const missingTotal = withoutStartDate.payro + withoutStartDate.nonPayro;

  return (
    <div
      style={{
        backgroundColor: "#fff",
        borderRadius: 10,
        padding: "24px 28px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        border: "1px solid #e8e8e8",
        margin: "16px 0 8px",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 20,
        }}
      >
        <div>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#1a1a2e" }}>
            Employee Seniority
          </h3>
          <p style={{ margin: "4px 0 0", fontSize: 12, color: "#888" }}>
            Distribution by hire date (startDate from phonebook)
          </p>
        </div>
        {/* Avg seniority chips */}
        <div style={{ display: "flex", gap: 12 }}>
          <div
            style={{
              background: "#f0faf0",
              border: "1px solid #c8e6c9",
              borderRadius: 8,
              padding: "8px 16px",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: 11, color: "#4CAF50", fontWeight: 600, marginBottom: 2 }}>
              Payro avg
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#2e7d32" }}>
              {formatAvgSeniority(avgMonths.payro)}
            </div>
          </div>
          <div
            style={{
              background: "#e8f0fe",
              border: "1px solid #bbdefb",
              borderRadius: 8,
              padding: "8px 16px",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: 11, color: "#1565c0", fontWeight: 600, marginBottom: 2 }}>
              Non-Payro avg
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#1565c0" }}>
              {formatAvgSeniority(avgMonths.nonPayro)}
            </div>
          </div>
        </div>
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={buckets} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="label" tick={{ fontSize: 12 }} />
          <YAxis
            tickFormatter={(v) => `${v}%`}
            domain={[0, 'auto']}
            tick={{ fontSize: 12 }}
            width={44}
          />
          <Tooltip content={<SeniorityTooltip />} />
          <Legend wrapperStyle={{ fontSize: 13 }} />
          <Bar dataKey="payroPercent" name="Payro Users" fill="#4CAF50" radius={[3, 3, 0, 0]} />
          <Bar dataKey="nonPayroPercent" name="Non-Payro" fill="#1565c0" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>

      {missingTotal > 0 && (
        <p style={{ margin: "10px 0 0", fontSize: 12, color: "#aaa" }}>
          * {withoutStartDate.payro.toLocaleString()} Payro / {withoutStartDate.nonPayro.toLocaleString()} non-Payro employees
          have no start date in the phonebook and are excluded from this chart.
        </p>
      )}
    </div>
  );
};

interface Props {
  queryParams: KpiQueryParams;
  companyName?: string;
}

const WorkforceAnalysis: React.FC<Props> = ({ queryParams, companyName }) => {
  const [data, setData] = useState<WorkforceAnalysisResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!queryParams.companyId) {
      setData(null);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    analyticsService
      .getWorkforceAnalysis(queryParams)
      .then((result) => {
        setData(result);
        setLoading(false);
      })
      .catch((err) => {
        const msg: string =
          err?.response?.data?.message ?? err?.message ?? "Unknown error";
        const isNoVault =
          msg.includes("vault") ||
          msg.includes("No such object") ||
          msg.includes("secret") ||
          err?.response?.status === 404;
        setError(isNoVault ? "no_vault" : msg);
        setLoading(false);
      });
  }, [queryParams.companyId, queryParams.database]);

  if (!queryParams.companyId) {
    return (
      <div
        style={{
          padding: 40,
          textAlign: "center",
          color: "#888",
          fontSize: 16,
        }}
      >
        Select a specific company to view workforce analysis.
      </div>
    );
  }

  if (error === "no_vault") {
    return (
      <div
        style={{
          padding: 40,
          textAlign: "center",
          color: "#888",
          fontSize: 16,
        }}
      >
        No vault data available for this company.
        <br />
        <span style={{ fontSize: 13, color: "#bbb" }}>
          Only integration-enabled companies with GCS vault files are supported.
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          padding: 40,
          textAlign: "center",
          color: "#ef5350",
          fontSize: 14,
        }}
      >
        Error: {error}
      </div>
    );
  }

  const cachedAt = data
    ? new Date(data.cachedAt).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  const gap = data?.coverageGap?.total ? data.coverageGap : null;

  const payroActive   = (data?.payro.active   ?? 0) + (gap?.payro.active   ?? 0);
  const payroLeft     = (data?.payro.left      ?? 0) + (gap?.payro.left     ?? 0);
  const payroTotal    = payroActive + payroLeft;
  const payroTurnover = payroTotal > 0 ? Math.round((payroLeft / payroTotal) * 1000) / 10 : 0;

  const gapNpActive = (gap?.nonPayro.active ?? 0) + (gap?.unknown.active ?? 0);
  const gapNpLeft   = (gap?.nonPayro.left   ?? 0) + (gap?.unknown.left   ?? 0);
  const npActive    = (data?.nonPayro.active ?? 0) + gapNpActive;
  const npLeft      = (data?.nonPayro.left   ?? 0) + gapNpLeft;
  const npTotal     = npActive + npLeft;
  const npTurnover  = npTotal > 0 ? Math.round((npLeft / npTotal) * 1000) / 10 : 0;

  // Blend normalized avg: (vault normalizedShifts + gap normalizedShifts) / new total
  // vault normalizedShifts = avg * count (since backend already divides by count)
  const payroAvgShifts = (() => {
    if (!data) return 0;
    if (!gap) return data.payro.avgShiftsPerMonth;
    const vaultNorm = data.payro.avgShiftsPerMonth * data.payro.total;
    return payroTotal > 0
      ? Math.round(((vaultNorm + gap.payro.normalizedShifts) / payroTotal) * 10) / 10
      : data.payro.avgShiftsPerMonth;
  })();

  const npAvgShifts = (() => {
    if (!data) return 0;
    if (!gap) return data.nonPayro.avgShiftsPerMonth;
    const vaultNorm = data.nonPayro.avgShiftsPerMonth * data.nonPayro.total;
    const gapNpNorm = (gap.nonPayro.normalizedShifts) + (gap.unknown.normalizedShifts);
    return npTotal > 0
      ? Math.round(((vaultNorm + gapNpNorm) / npTotal) * 10) / 10
      : data.nonPayro.avgShiftsPerMonth;
  })();

  const gapAttendancePeriod = data?.attendancePeriod
    ? `${data.attendancePeriod.start} – ${data.attendancePeriod.end}`
    : null;

  const gapNonPayroCount = (gap?.nonPayro.count ?? 0) + (gap?.unknown.count ?? 0);

  return (
    <div id="workforce-print-section" style={{ padding: "0 0 40px" }}>
      <style>{PRINT_CSS}</style>
      {/* Print-only header */}
      <div className="print-header" style={{ marginBottom: 20, paddingBottom: 16, borderBottom: "1px solid #ddd" }}>
        <div style={{ fontWeight: 700, fontSize: 18, color: "#1a1a2e" }}>Workforce Analysis Report</div>
        <div style={{ fontSize: 13, color: "#555", marginTop: 4 }}>
          {companyName ?? `Company ${queryParams.companyId}`}
          &nbsp;·&nbsp;
          Generated: {new Date().toLocaleString()}
        </div>
        {data?.attendancePeriod && (
          <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
            Data period: {data.attendancePeriod.start} – {data.attendancePeriod.end} ({data.monthsInData} months)
          </div>
        )}
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        }}
      >
        <h2 style={{ margin: 0, color: "#1a1a2e", fontSize: 22 }}>
          Vault-Based Workforce Analysis
        </h2>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {cachedAt && (
            <span
              className="print-hide"
              style={{
                fontSize: 12,
                color: "#888",
                background: "#f5f5f5",
                padding: "4px 10px",
                borderRadius: 20,
              }}
            >
              Cached at {cachedAt}
            </span>
          )}
          <button
            className="print-hide"
            onClick={() => {
              const now = new Date();
              const dd = String(now.getDate()).padStart(2, "0");
              const mm = String(now.getMonth() + 1).padStart(2, "0");
              const yyyy = now.getFullYear();
              const time = `${String(now.getHours()).padStart(2, "0")}.${String(now.getMinutes()).padStart(2, "0")}`;
              const safeName = (
                (companyName ?? "")
                  .trim()
                  .replace(/\s+/g, "_")
                  .replace(/[/\\:*?"<>|]/g, "")
              ) || `company_${queryParams.companyId}`;
              const prev = document.title;
              document.title = `${safeName}_${dd}-${mm}-${yyyy}_${time}`;
              window.addEventListener("afterprint", () => { document.title = prev; }, { once: true });
              window.print();
            }}
            disabled={loading || !data}
            style={{
              padding: "6px 14px",
              background: loading || !data ? "#e0e0e0" : "#1a1a2e",
              color: loading || !data ? "#aaa" : "#fff",
              border: "none",
              borderRadius: 6,
              cursor: loading || !data ? "default" : "pointer",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            🖨 Save as PDF
          </button>
        </div>
      </div>

      {data && (
        <div style={{ marginBottom: 16, fontSize: 13, color: "#888" }}>
          Vault total: <strong>{data.totalVault.toLocaleString()}</strong>{" "}
          employees &nbsp;·&nbsp;
          <strong>{data.excluded.toLocaleString()}</strong> excluded (no attendance record since data coverage began)
          {gap && gap.total > 0 && (
            <span style={{ marginLeft: 8, color: "#d97706" }}>
              &nbsp;·&nbsp; +{gap.total.toLocaleString()} attendance-only employees included below
            </span>
          )}
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 24,
        }}
      >
        <StatCard
          title='Payro Users'
          description={DESCRIPTIONS.payro.card}
          accentColor='#4CAF50'
          total={loading ? 0 : payroTotal}
          active={loading ? 0 : payroActive}
          activeDesc={DESCRIPTIONS.payro.active}
          left={loading ? 0 : payroLeft}
          leftDesc={DESCRIPTIONS.payro.left}
          turnoverPct={loading ? 0 : payroTurnover}
          loading={loading}
        />
        <StatCard
          title='Non-Payro Employees'
          description={gap && gap.total > 0
            ? `Non-Payro employees (vault + ${gapNonPayroCount} attendance-only employees not in vault)`
            : DESCRIPTIONS.nonPayro.card}
          accentColor='#1565c0'
          total={loading ? 0 : npTotal}
          active={loading ? 0 : npActive}
          activeDesc={DESCRIPTIONS.nonPayro.active}
          left={loading ? 0 : npLeft}
          leftDesc={DESCRIPTIONS.nonPayro.left}
          turnoverPct={loading ? 0 : npTurnover}
          loading={loading}
        />
        {!loading && data && (
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: 10,
              padding: "28px 32px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              border: "2px solid #e0e0e0",
            }}
          >
            <h3
              style={{
                margin: "0 0 20px",
                color: "#555",
                fontSize: 16,
                fontWeight: 700,
              }}
            >
              Combined
            </h3>
            <div
              style={{
                fontSize: 40,
                fontWeight: 800,
                color: "#1a1a2e",
                marginBottom: 16,
              }}
            >
              {(payroTotal + npTotal).toLocaleString()}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <StatRow
                label='Total Active'
                value={payroActive + npActive}
                color='#4CAF50'
              />
              <StatRow
                label='Total Left'
                value={payroLeft + npLeft}
                color='#ef5350'
              />
              <div
                style={{
                  marginTop: 8,
                  paddingTop: 8,
                  borderTop: "1px solid #eee",
                }}
              >
                <span style={{ fontSize: 13, color: "#888" }}>
                  Payro adoption:{" "}
                </span>
                <span
                  style={{ fontSize: 15, fontWeight: 700, color: "#4CAF50" }}
                >
                  {payroTotal + npTotal > 0
                    ? Math.round((payroTotal / (payroTotal + npTotal)) * 1000) / 10
                    : 0}
                  %
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Avg shifts per month row */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 24,
          marginTop: 16,
        }}
      >
        {!loading && data ? (
          <>
            <ShiftCard
              title='Avg Shifts/Month — Payro Users'
              accentColor='#4CAF50'
              value={payroAvgShifts}
              months={data.monthsInData}
            />
            <ShiftCard
              title='Avg Shifts/Month — Non-Payro'
              accentColor='#1565c0'
              value={npAvgShifts}
              months={data.monthsInData}
            />
            <div />
          </>
        ) : (
          <>
            <div
              style={{
                backgroundColor: "#fff",
                borderRadius: 10,
                padding: "20px 32px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              }}
            >
              <SkeletonBar width='50%' height={14} />
              <SkeletonBar width='30%' height={28} />
            </div>
            <div
              style={{
                backgroundColor: "#fff",
                borderRadius: 10,
                padding: "20px 32px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              }}
            >
              <SkeletonBar width='50%' height={14} />
              <SkeletonBar width='30%' height={28} />
            </div>
            <div />
          </>
        )}
      </div>
      {/* Seniority Analysis */}
      {!loading && data?.seniority && <SenioritySection seniority={data.seniority} />}

      {/* Vault Coverage Gap — dynamic, shown for any company that has one */}
      {!loading && gap && gap.total > 0 && (
        <div
          style={{
            border: "1.5px solid #f59e0b",
            borderRadius: 10,
            overflow: "hidden",
            margin: "24px 0 8px",
          }}
        >
          {/* Header */}
          <div
            style={{
              backgroundColor: "#fffbeb",
              padding: "14px 20px",
              borderBottom: "1px solid #fde68a",
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <span style={{ fontSize: 16 }}>⚠</span>
            <div>
              <span style={{ fontWeight: 700, fontSize: 14, color: "#92400e" }}>
                Vault Coverage Gap — {gap.total.toLocaleString()} employees
              </span>
              <span style={{ fontSize: 12, color: "#b45309", marginLeft: 10 }}>
                found in attendance data{gapAttendancePeriod ? ` (${gapAttendancePeriod})` : ""} but missing from current vault file
              </span>
            </div>
            <span
              style={{
                marginLeft: "auto",
                fontSize: 11,
                color: "#d97706",
                background: "#fef3c7",
                border: "1px solid #fde68a",
                borderRadius: 20,
                padding: "2px 10px",
                whiteSpace: "nowrap",
              }}
            >
              source: attendance × vault cross-reference
            </span>
          </div>

          {/* Two columns: Active | Left */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", backgroundColor: "#fff" }}>
            {/* Active column */}
            <div style={{ padding: "20px 24px", borderRight: "1px solid #fde68a" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#4CAF50", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>
                Active
              </div>
              <div style={{ fontSize: 12, color: "#aaa", marginBottom: 14 }}>last shift within 60 days</div>
              <div style={{ fontSize: 32, fontWeight: 800, color: "#1a1a2e", marginBottom: 14 }}>
                {(gap.payro.active + gap.nonPayro.active + gap.unknown.active).toLocaleString()}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 13, color: "#555" }}>
                    Payro users
                    <span style={{ fontSize: 11, color: "#bbb", marginLeft: 6 }}>(confirmed)</span>
                  </span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: "#4CAF50" }}>{gap.payro.active}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 13, color: "#555" }}>Non-Payro</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: "#1565c0" }}>{gap.nonPayro.active}</span>
                </div>
                {gap.unknown.count > 0 && (
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 13, color: "#888" }}>
                      Unknown
                      <span style={{ fontSize: 11, color: "#bbb", marginLeft: 6 }}>(no govId in attendance)</span>
                    </span>
                    <span style={{ fontSize: 14, fontWeight: 700, color: "#aaa" }}>{gap.unknown.active}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Left column */}
            <div style={{ padding: "20px 24px" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#ef5350", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>
                Left
              </div>
              <div style={{ fontSize: 12, color: "#aaa", marginBottom: 14 }}>last shift 60+ days ago</div>
              <div style={{ fontSize: 32, fontWeight: 800, color: "#1a1a2e", marginBottom: 14 }}>
                {(gap.payro.left + gap.nonPayro.left + gap.unknown.left).toLocaleString()}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 13, color: "#555" }}>
                    Payro users
                    <span style={{ fontSize: 11, color: "#bbb", marginLeft: 6 }}>(confirmed)</span>
                  </span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: "#ef5350" }}>{gap.payro.left}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 13, color: "#555" }}>Non-Payro</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: "#1565c0" }}>{gap.nonPayro.left}</span>
                </div>
                {gap.unknown.count > 0 && (
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 13, color: "#888" }}>
                      Unknown
                      <span style={{ fontSize: 11, color: "#bbb", marginLeft: 6 }}>(no govId in attendance)</span>
                    </span>
                    <span style={{ fontSize: 14, fontWeight: 700, color: "#aaa" }}>{gap.unknown.left}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer note */}
          <div style={{ backgroundColor: "#fffbeb", borderTop: "1px solid #fde68a", padding: "10px 20px", fontSize: 12, color: "#92400e" }}>
            These employees have attendance records but are absent from the current vault file.
            {gap.unknown.count > 0
              ? ` Payro status confirmed for ${(gap.payro.count + gap.nonPayro.count).toLocaleString()} of ${gap.total.toLocaleString()} via govId lookup. ${gap.unknown.count.toLocaleString()} could not be classified (attendance uses internal IDs, not govIds).`
              : ` Payro status confirmed for all ${gap.total.toLocaleString()} via govId→DB lookup.`
            }
          </div>
        </div>
      )}

      {/* Methodology note */}
      <div
        style={{
          backgroundColor: "#f8f9fc",
          border: "1px solid #e8eaf0",
          borderRadius: 8,
          padding: "16px 20px",
          margin: "24px 0",
          fontSize: 13,
          color: "#555",
          lineHeight: 1.7,
        }}
      >
        <div style={{ fontWeight: 700, color: "#333", marginBottom: 6 }}>
          How this data is calculated
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <div>
            <span style={{ color: "#888" }}>Employee list —</span> Decrypted
            from the company's encrypted vault file (GCS). This is the full
            roster synced from the company's HR/attendance system.
          </div>
          <div>
            <span style={{ color: "#888" }}>Payro vs Non-Payro —</span> An
            employee is a Payro User if their phone number matches a registered
            user in the DB (type=2). Everyone else is Non-Payro.
          </div>
          <div>
            <span style={{ color: "#888" }}>Active vs Left —</span> Based on
            attendance files (GCS, Sep 2025 onwards). Last file of each month is
            used. An employee is Active if their most recent shift was within 60
            days; Left if 60+ days ago.
          </div>
          <div>
            <span style={{ color: "#888" }}>Excluded —</span> Employees with no
            attendance record at all — assumed to have left before Sep 2025
            (start of data coverage).
          </div>
          <div>
            <span style={{ color: "#888" }}>Avg shifts/month —</span> Total
            unique shift dates per employee divided by the number of months with
            data.
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkforceAnalysis;
